import { type RefObject, useEffect, useState } from "react"

// Same dead band as `useFittedTableHeight`: a body that resizes can nudge what
// sits above it by a pixel or two, and without a tolerance those nudges feed
// back into the next measurement.
const TOLERANCE = 8

/**
 * How tall the table would render if nothing capped it.
 *
 * `useFittedTableHeight` answers how much room the body may *take*; this
 * answers how much it actually *needs*, so a short result set can hug its rows
 * instead of leaving a blank rectangle under them. A fixed column forces
 * AntD's synced-scroll body into a real height rather than a max-height, which
 * is why the difference is visible at all.
 *
 * Non-circular despite measuring the thing being sized: the element read is
 * the `<table>` *inside* the scroll container, whose height is its rows' own
 * and is not clamped by the `scroll.y` applied to its parent.
 */
export function useTableContentHeight<T extends HTMLElement>(
  containerRef: RefObject<T | null>
): number {
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === "undefined") return

    const measure = () => {
      const header = container.querySelector(".ant-table-header")
      const body = container.querySelector(".ant-table-body")
      const content = body?.firstElementChild ?? body

      if (!content) return

      const next =
        (header?.getBoundingClientRect().height ?? 0) +
        content.getBoundingClientRect().height

      setHeight((current) =>
        Math.abs(current - next) > TOLERANCE ? next : current
      )
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [containerRef])

  return height
}
